// ============================================================
// FILE: ReviewsTable.js
// LOCATION: C:\gardenmarket\web\src\components\ReviewsTable.js
// ============================================================
// PURPOSE:
//   Shared reviews table — "From"/"About" computed relative to
//   the logged-in viewer (see original design rationale below).
//   Visual style now uses theme.js's `card`/`cardHeader` fragments
//   for consistency with every other detail-pane card.
//
// DESIGN RATIONALE: labelling a review by who WROTE it answers
//   the wrong question — what matters is "is this about me, or
//   did I write it?" relative to the viewer. The row about the
//   viewer is highlighted with a soft tint.
//
// PROPS:
//   reviews        — array from GET /api/reviews/job/:id
//   currentUserId  — user.user_id of whoever is logged in
// ============================================================

import { colors, card, cardHeader } from '../theme/theme';

export default function ReviewsTable({ reviews, currentUserId }) {
    if (!reviews || reviews.length === 0) return null;

    return (
        <div style={card}>
            <h3 style={cardHeader}>Reviews</h3>
            <table style={styles.table}>
                <thead>
                    <tr style={styles.headRow}>
                        <th style={styles.th}>From</th>
                        <th style={styles.th}>About</th>
                        <th style={styles.thRating}>Rating</th>
                        <th style={styles.th}>Comment</th>
                    </tr>
                </thead>
                <tbody>
                    {reviews.map(r => {
                        const isAboutMe = r.reviewee_user_id === currentUserId;
                        const isFromMe  = r.reviewer_user_id === currentUserId;

                        return (
                            <tr key={r.review_id} style={isAboutMe ? styles.highlightRow : styles.row}>
                                <td style={styles.td}>
                                    {isFromMe ? 'You' : (
                                        <>
                                            {r.reviewer_name}
                                            <span style={styles.roleTag}>
                                                ({r.reviewer_role === 'buyer' ? 'buyer' : 'gardener'})
                                            </span>
                                        </>
                                    )}
                                </td>
                                <td style={styles.td}>
                                    {isAboutMe ? 'You' : (
                                        <>
                                            {r.reviewee_name}
                                            <span style={styles.roleTag}>
                                                ({r.reviewer_role === 'buyer' ? 'gardener' : 'buyer'})
                                            </span>
                                        </>
                                    )}
                                </td>
                                <td style={styles.tdRating}>
                                    {'★'.repeat(r.star_rating)}{'☆'.repeat(5 - r.star_rating)}
                                </td>
                                <td style={styles.tdComment}>
                                    {r.comment ? `"${r.comment}"` : <span style={{color: colors.textFaint}}>—</span>}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            <p style={styles.hint}>The review about you is highlighted.</p>
        </div>
    );
}

const styles = {
    table:        { width:'100%', borderCollapse:'collapse', fontSize:'0.85rem', tableLayout:'fixed' },
    headRow:      { borderBottom:`1px solid ${colors.border}` },
    th:           { textAlign:'left', padding:'6px', color: colors.textFaint, fontWeight:600, fontSize:'0.74rem' },
    thRating:     { textAlign:'left', padding:'6px', color: colors.textFaint, fontWeight:600, fontSize:'0.74rem', width:'85px' },
    row:          { borderBottom:`1px solid ${colors.border}` },
    highlightRow: { borderBottom:`1px solid ${colors.border}`, backgroundColor: colors.selectedTint, borderRadius: '8px' },
    td:           { padding:'10px 6px', verticalAlign:'top', color: colors.text, fontWeight:500 },
    tdRating:     { padding:'10px 6px', verticalAlign:'top', color:'#f5a623', whiteSpace:'nowrap' },
    tdComment:    { padding:'10px 6px', verticalAlign:'top', color: colors.textMuted, fontStyle:'italic' },
    roleTag:      { display:'block', fontSize:'0.72rem', color: colors.textFaint, fontWeight:400 },
    hint:         { fontSize:'0.76rem', color: colors.textFaint, marginTop:'12px', marginBottom:0 },
};
